import { DeliveryByWeekday } from "./calendar.js";
import { z } from "zod";
import { Centavos, IsoDate, IsoDateTime, QueryBool, Uuid } from "./common.js";

// ---- Patient instruction images and the WhatsApp quote (pilot request 2026-11-02, migration 0019) ----
//
// The laboratory answers price questions in WhatsApp: a typed message with the price and the
// preparation, plus one of a few images ("Recolección de orina"…) that it keeps on the phone.
// The images become a lab-level library (Configuración → Indicaciones para pacientes), each one
// optionally linked to the studies it applies to, and a quote opens a WhatsApp hand-off that
// pre-writes the message and pre-selects the images of the quoted studies.

/** Caps shared by the web app (which checks before uploading) and the API (which refuses anything else). */
export const INSTRUCTION_IMAGE_LIMITS = {
  maxBytes: 2 * 1024 * 1024,
  maxNameLength: 80,
  maxStudies: 200,
} as const;

export const InstructionImageContentType = z.enum(["image/png", "image/jpeg"]);
export type InstructionImageContentType = z.infer<typeof InstructionImageContentType>;

const Base64 = z.string().regex(/^[A-Za-z0-9+/]+={0,2}$/, "Imagen inválida.");
const ImageName = z.string().trim().min(1).max(INSTRUCTION_IMAGE_LIMITS.maxNameLength);

/** `instruction_image.create` (Administrador): a new image. The bytes are immutable once stored. */
export const InstructionImageCreatePayload = z.object({
  name: ImageName,
  content_type: InstructionImageContentType,
  image_base64: Base64.min(8).max(Math.ceil(INSTRUCTION_IMAGE_LIMITS.maxBytes / 3) * 4),
  /** Studies the image applies to; a quote with any of them pre-selects it. */
  study_ids: z.array(Uuid).max(INSTRUCTION_IMAGE_LIMITS.maxStudies).default([]),
});
export type InstructionImageCreatePayload = z.infer<typeof InstructionImageCreatePayload>;

/** `instruction_image.update` (Administrador): rename, relink, retire or restore. Never the bytes. */
export const InstructionImageUpdatePayload = z.object({
  image_id: Uuid,
  name: ImageName.optional(),
  study_ids: z.array(Uuid).max(INSTRUCTION_IMAGE_LIMITS.maxStudies).optional(),
  is_active: z.boolean().optional(),
});
export type InstructionImageUpdatePayload = z.infer<typeof InstructionImageUpdatePayload>;

/** Metadata of a stored image; the bytes travel only in `GET /v1/instruction-images/:id/file`. */
export const InstructionImage = z.object({
  id: Uuid,
  name: z.string(),
  content_type: InstructionImageContentType,
  byte_size: z.number().int().positive(),
  sha256_hex: z.string().regex(/^[0-9a-f]{64}$/),
  study_ids: z.array(Uuid),
  is_active: z.boolean(),
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type InstructionImage = z.infer<typeof InstructionImage>;

export const InstructionImageListQuery = z.object({
  /** Retired images too (the Configuración list); the quote dialog reads active ones only. */
  include_inactive: QueryBool.default(false),
});
export type InstructionImageListQuery = z.infer<typeof InstructionImageListQuery>;

export const InstructionImageList = z.object({ items: z.array(InstructionImage) });
export type InstructionImageList = z.infer<typeof InstructionImageList>;

/** One quoted line as the WhatsApp message needs it. */
export const QuoteWhatsAppLine = z.object({
  kind: z.enum(["study", "package"]),
  name: z.string(),
  /** The line's preparation as frozen on the quote ("Ayuno de 8 horas"). */
  indications: z.string().nullable(),
  /** Turnaround from the current catalogue; a package takes its slowest study. null = not set. */
  delivery_days: z.number().int().min(0).nullable(),
  /** Weekday tables (ADR 0027) of the line's studies; the message promises the latest for today. */
  delivery_by_weekday: z.array(DeliveryByWeekday).optional(),
  /** The study itself, or the package's studies: what an image's study links match against. */
  study_ids: z.array(Uuid),
});
export type QuoteWhatsAppLine = z.infer<typeof QuoteWhatsAppLine>;

/** GET /v1/quotes/:id/whatsapp — what the hand-off composes the message from. */
export const QuoteWhatsApp = z.object({
  quote_id: Uuid,
  folio: z.string(),
  /** The registered patient's first name, or the prospect's first word; null when unknown. */
  first_name: z.string().nullable(),
  /** The registered patient's phone (10 digits when it has them); null for a prospect. */
  phone: z.string().nullable(),
  lines: z.array(QuoteWhatsAppLine),
  total_cents: Centavos,
  valid_until: IsoDate.nullable(),
});
export type QuoteWhatsApp = z.infer<typeof QuoteWhatsApp>;

/**
 * `quote.whatsapp_sent`: the counter opened the WhatsApp chat with the quote's message. Written to
 * the audit chain with the quote and the image ids only — never the phone or the text.
 */
export const QuoteWhatsAppSentPayload = z.object({
  quote_id: Uuid,
  image_ids: z.array(Uuid).max(20).default([]),
});
export type QuoteWhatsAppSentPayload = z.infer<typeof QuoteWhatsAppSentPayload>;
